import { MONTHS, MONTH_ALIASES, MONTH_TOKENS, QUARTERS, REGIONS, YEARS } from "./constants";
import type {
  GeographyScope,
  ReportHeaderBinding,
  ReportHeaderConflict,
  ReportHeaderField,
  ReportMeta,
  ReportRequest,
  ReportRequestError,
  ReportRequestParams,
  ReportRequestResolution,
  TemplateType,
} from "./types";

export const ETHIOPIAN_YEAR_SUFFIX = "E.C.";

const REPORT_KIND = "Health Performance Report";

const NATIONAL_GEOGRAPHY_LABEL = "National Level";

const CADENCE_LABELS: Record<TemplateType, string> = {
  yearly: "Annual",
  quarterly: "Quarterly",
  monthly: "Monthly",
};

const SORTED_REGIONS = [...REGIONS].sort((a, b) => b.length - a.length);

const YEAR_PATTERN = /(?:^|[^0-9])((?:19|20)[0-9]{2})(?![0-9])/g;
const QUARTER_PATTERN = /\bQ([1-4])\b/gi;
const MONTH_PATTERN = new RegExp(`\\b(?:${MONTH_TOKENS.join("|")})\\b`, "gi");
const CADENCE_PATTERNS: ReadonlyArray<{ cadence: TemplateType; pattern: RegExp }> = [
  { cadence: "monthly", pattern: /\b(?:monthly|month)\b/gi },
  { cadence: "quarterly", pattern: /\b(?:quarterly|quarter)\b/gi },
  { cadence: "yearly", pattern: /\b(?:annual|yearly|annually|year)\b/gi },
];
const NATIONAL_SCOPE_PATTERN =
  /\b(?:national|national level|nationwide|countrywide|federal|ethiopia|all regions)\b/i;

export interface ReportRequestOptions {
  nationalGeographyLabel?: string;
  availableYears?: string[];
}

export class ReportRequestUnavailableError extends Error {
  readonly detail: ReportRequestError;

  constructor(detail: ReportRequestError) {
    super(detail.message);
    this.name = "ReportRequestUnavailableError";
    this.detail = detail;
  }
}

interface ResolvedPeriod {
  year: string;
  quarter: string | null;
  month: string | null;
}

interface ResolvedGeography {
  geography: string;
  geographyScope: GeographyScope;
  region: string | null;
}

const trimValue = (value: unknown): string => String(value ?? "").trim();

const titleCase = (value: string): string =>
  value.replace(/\b\w/g, (character) => character.toUpperCase());

const periodError = (code: string, message: string): ReportRequestError => ({
  field: "period",
  code,
  message,
});

const geographyError = (code: string, message: string): ReportRequestError => ({
  field: "geography",
  code,
  message,
});

export const formatEthiopianYear = (ethiopianYear: string): string =>
  `${trimValue(ethiopianYear)} ${ETHIOPIAN_YEAR_SUFFIX}`;

const canonicalMonth = (value: string): string | null => {
  const key = trimValue(value).toLowerCase();
  if (!key) return null;
  const canonical = MONTHS.find((month) => month.toLowerCase() === key);
  if (canonical) return canonical;
  return MONTH_ALIASES[key] ?? null;
};

const composePeriod = (
  cadence: TemplateType,
  year: string,
  quarter: string | null,
  month: string | null,
): string => {
  const yearLabel = formatEthiopianYear(year);
  if (cadence === "quarterly" && quarter) return `${quarter} ${yearLabel}`;
  if (cadence === "monthly" && month) return `${month} ${yearLabel}`;  return yearLabel;
};

const collectMatches = (
  text: string,
  pattern: RegExp,
  project: (match: RegExpExecArray) => string,
): string[] => {
  const scanner = new RegExp(pattern.source, pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`);
  const values = new Set<string>();
  let match = scanner.exec(text);
  while (match) {
    values.add(project(match));
    match = scanner.exec(text);
  }
  return [...values];
};

const mentionedYears = (text: string): string[] =>
  collectMatches(text, YEAR_PATTERN, (match) => match[1]);

const mentionedQuarters = (text: string): string[] =>
  collectMatches(text, QUARTER_PATTERN, (match) => `Q${match[1].toUpperCase()}`);

const mentionedMonths = (text: string): string[] => {
  const values = new Set<string>();
  for (const match of collectMatches(text, MONTH_PATTERN, (found) => found[0])) {
    const canonical = canonicalMonth(match);
    if (canonical) values.add(canonical);
  }
  return [...values];
};

const mentionedRegions = (text: string): string[] => {
  const source = text.toLowerCase();
  return SORTED_REGIONS.filter((region) => source.includes(region.toLowerCase()));
};

const mentionedCadences = (text: string): TemplateType[] =>
  CADENCE_PATTERNS.filter(
    ({ pattern }) => collectMatches(text, pattern, () => "match").length > 0,
  ).map(({ cadence }) => cadence);

export const resolveReportPeriod = (
  params: ReportRequestParams,
  availableYears: string[] = YEARS,
): ResolvedPeriod | { error: ReportRequestError } => {
  const year = trimValue(params.year);
  if (!year) {
    return {
      error: periodError(
        "period.missingYear",
        "No reporting year is selected, so the reporting period cannot be resolved. Select a year under Parameters.",
      ),
    };
  }

  if (!/^[0-9]{4}$/.test(year)) {
    return {
      error: periodError(
        "period.invalidYear",
        `"${year}" is not a valid Ethiopian fiscal year.`,
      ),
    };
  }

  if (availableYears.length > 0 && !availableYears.includes(year)) {
    const first = availableYears[0];
    const last = availableYears[availableYears.length - 1];
    return {
      error: periodError(
        "period.unavailableYear",
        `The requested period ${formatEthiopianYear(year)} is not available. Available periods run from ${formatEthiopianYear(first)} to ${formatEthiopianYear(last)}.`,
      ),
    };
  }

  if (params.template === "quarterly") {
    const quarter = trimValue(params.quarter).toUpperCase();
    if (!QUARTERS.includes(quarter)) {
      return {
        error: periodError(
          "period.missingQuarter",
          `A quarterly report for ${formatEthiopianYear(year)} was requested but no quarter (Q1-Q4) is selected.`,
        ),
      };
    }
    return { year, quarter, month: null };
  }

  if (params.template === "monthly") {
    const month = canonicalMonth(params.month);
    if (!month) {
      return {
        error: periodError(
          "period.missingMonth",
          `A monthly report for ${formatEthiopianYear(year)} was requested but no month is selected.`,
        ),
      };
    }
    return { year, quarter: null, month };
  }

  return { year, quarter: null, month: null };
};

export const resolveReportGeography = (
  params: ReportRequestParams,
  options: ReportRequestOptions = {},
): ResolvedGeography | { error: ReportRequestError } => {
  if (params.regionType === "national") {
    return {
      geography: options.nationalGeographyLabel || NATIONAL_GEOGRAPHY_LABEL,
      geographyScope: "national",
      region: null,
    };
  }

  const region = trimValue(params.selectedRegion);
  if (!region) {
    return {
      error: geographyError(
        "geography.missingRegion",
        "A regional report was requested but no region is selected. Choose a region under Geography.",
      ),
    };
  }

  const canonicalRegion = REGIONS.find(
    (item) => item.toLowerCase() === region.toLowerCase(),
  );
  if (!canonicalRegion) {
    return {
      error: geographyError(
        "geography.unavailableRegion",
        `The requested region "${region}" is not an available reporting geography. Available regions: ${REGIONS.join(", ")}.`,
      ),
    };
  }

  return { geography: canonicalRegion, geographyScope: "regional", region: canonicalRegion };
};

const composeReportRequest = (
  cadence: TemplateType,
  period: ResolvedPeriod,
  geography: ResolvedGeography,
): ReportRequest => ({
  cadence,
  cadenceLabel: CADENCE_LABELS[cadence],
  year: period.year,
  quarter: period.quarter,
  month: period.month,
  period: composePeriod(cadence, period.year, period.quarter, period.month),
  geographyScope: geography.geographyScope,
  region: geography.region,
  geography: geography.geography,
  title: `${CADENCE_LABELS[cadence]} ${REPORT_KIND}`,
});

export const tryBuildReportRequest = (
  params: ReportRequestParams,
  options: ReportRequestOptions = {},
): ReportRequestResolution => {
  const period = resolveReportPeriod(params, options.availableYears ?? YEARS);
  if ("error" in period) return { ok: false, error: period.error };

  const geography = resolveReportGeography(params, options);
  if ("error" in geography) return { ok: false, error: geography.error };

  return { ok: true, request: composeReportRequest(params.template, period, geography) };
};

export const buildReportRequest = (
  params: ReportRequestParams,
  options: ReportRequestOptions = {},
): ReportRequest => {
  const resolution = tryBuildReportRequest(params, options);
  if (!resolution.ok) throw new ReportRequestUnavailableError(resolution.error);
  return resolution.request;
};

export const describeHeaderConflicts = (
  request: ReportRequest,
  text: string,
  field: ReportHeaderField,
): ReportHeaderConflict[] => {
  const source = trimValue(text);
  if (!source) return [];

  const conflicts: ReportHeaderConflict[] = [];

  const foreignYears = mentionedYears(source).filter((year) => year !== request.year);
  if (foreignYears.length > 0) {
    conflicts.push({ field, requested: request.period, generated: foreignYears.join(", ") });
  }

  const quarters = mentionedQuarters(source);
  if (quarters.length > 0) {
    if (request.cadence !== "quarterly") {
      conflicts.push({ field, requested: request.cadenceLabel, generated: quarters.join(", ") });
    } else {
      const requestedQuarter = (request.quarter ?? "").toUpperCase();
      const foreignQuarters = quarters.filter((quarter) => quarter !== requestedQuarter);
      if (foreignQuarters.length > 0) {
        conflicts.push({ field, requested: request.period, generated: foreignQuarters.join(", ") });
      }
    }
  }

  const months = mentionedMonths(source);
  if (months.length > 0) {
    if (request.cadence !== "monthly") {
      conflicts.push({
        field,
        requested: request.cadenceLabel,
        generated: months.map(titleCase).join(", "),
      });
    } else {
      const requestedMonth = (request.month ?? "").toLowerCase();
      const foreignMonths = months.filter((month) => month.toLowerCase() !== requestedMonth);
      if (foreignMonths.length > 0) {
        conflicts.push({
          field,
          requested: request.period,
          generated: foreignMonths.map(titleCase).join(", "),
        });
      }
    }
  }

  const foreignCadences = mentionedCadences(source).filter(
    (cadence) => cadence !== request.cadence,
  );
  if (foreignCadences.length > 0) {
    conflicts.push({
      field,
      requested: request.cadenceLabel,
      generated: foreignCadences.map((cadence) => CADENCE_LABELS[cadence]).join(", "),
    });
  }

  const regions = mentionedRegions(source);
  if (request.geographyScope === "national") {
    if (regions.length > 0) {
      conflicts.push({ field, requested: request.geography, generated: regions.join(", ") });
    }
  } else {
    const requestedRegion = (request.region ?? "").toLowerCase();
    const foreignRegions = regions.filter((region) => region.toLowerCase() !== requestedRegion);
    if (foreignRegions.length > 0) {
      conflicts.push({ field, requested: request.geography, generated: foreignRegions.join(", ") });
    } else if (
      NATIONAL_SCOPE_PATTERN.test(source) &&
      !regions.some((region) => region.toLowerCase() === requestedRegion)
    ) {
      conflicts.push({ field, requested: request.geography, generated: source });
    }
  }

  return conflicts;
};

export const bindReportHeader = (
  request: ReportRequest,
  meta?: ReportMeta | null,
): ReportHeaderBinding => {
  const generatedTitle = trimValue(meta?.title);
  const generatedType = trimValue(meta?.reportType);
  const conflicts: ReportHeaderConflict[] = [];

  if (meta?.timePeriod) {
    conflicts.push(...describeHeaderConflicts(request, meta.timePeriod, "period"));
  }
  if (meta?.geography) {
    conflicts.push(...describeHeaderConflicts(request, meta.geography, "geography"));
  }

  const titleBlob = [generatedTitle, generatedType].filter(Boolean).join(" ");
  const titleConflicts = titleBlob
    ? describeHeaderConflicts(request, titleBlob, "title")
    : [];
  conflicts.push(...titleConflicts);

  return {
    title: generatedTitle && titleConflicts.length === 0 ? generatedTitle : request.title,
    periodText: request.period,
    geographyText: request.geography,
    boundToRequest: true,
    conflicts,
  };
};

export const reportRequestFromMeta = (meta?: {
  geography?: string | null;
  timePeriod?: string | null;
} | null): ReportRequest | null => {
  const timePeriod = trimValue(meta?.timePeriod);
  const years = mentionedYears(timePeriod);
  if (years.length === 0) return null;

  const year = years[0];
  const quarters = mentionedQuarters(timePeriod);
  const months = mentionedMonths(timePeriod);
  const cadence: TemplateType = quarters.length > 0
    ? "quarterly"
    : months.length > 0
      ? "monthly"
      : "yearly";

  const geographySource = trimValue(meta?.geography);
  const region = mentionedRegions(geographySource)[0] ?? null;
  const geographyScope: GeographyScope = region ? "regional" : "national";
  const geography = geographySource || NATIONAL_GEOGRAPHY_LABEL;

  return composeReportRequest(
    cadence,
    {
      year,
      quarter: quarters[0] ?? null,
      month: months.length > 0 ? canonicalMonth(months[0]) : null,
    },
    { geography, geographyScope, region },
  );
};
